import { useEffect, useMemo, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import Sidebar from "../../components/Sidebar";
import { usePermissions } from "../../context/PermissionsContext";
import { getPermissions } from "../../services/admin/getPermissions";
import { getRolePermissions } from "../../services/admin/getRolePermissions";
import { updateRolePermissions } from "../../services/admin/updateRolePermissions";

const ROLES = ["Admin", "User"];
const DISPLAY_MODULES = [
    { key: "clients", title: "Clients" },
    { key: "client_services", title: "Services" },
    { key: "steps", title: "Steps" },
    { key: "departments", title: "Departments" },
    { key: "pipelines", title: "Pipelines" },
    { key: "teams", title: "Teams" },
    { key: "team_roles", title: "Team Roles" },
    { key: "services", title: "Service Templates" },
];
const DISPLAY_ACTIONS = [
    { key: "read", label: "Read" },
    { key: "write", label: "Edit" },
    { key: "delete", label: "Delete" },
];

const normalizeKey = (value) =>
    typeof value === "string" ? value.trim().toLowerCase() : "";

const toTitleCase = (value) =>
    String(value || "")
        .replace(/[._-]/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

// Verified AdminController responses: string[] grants or grouped catalog.
const normalizePermissionValues = (payload) => {
    if (!Array.isArray(payload)) return [];
    return payload.flatMap((item) => typeof item === "string"
        ? [normalizeKey(item)]
        : (item.permissions || []).map((permission) => normalizeKey(permission.key))
    ).filter(Boolean);
};

function Permissions() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState("Admin");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [descriptions, setDescriptions] = useState({});
    const [availablePermissions, setAvailablePermissions] = useState(new Set());
    const [checkedPermissions, setCheckedPermissions] = useState(new Set());

    const { can, permissionsLoading, refreshPermissions } = usePermissions();
    // The enclosing AdminRoute also verifies the authenticated Admin role.
    const canManage = !permissionsLoading && can("permissions.manage");
    const [loadedRole, setLoadedRole] = useState(null);
    const [savedPermissions, setSavedPermissions] = useState(new Set());
    const saveInFlight = useRef(false);
    const loadVersion = useRef(0);

    const getErrorMessage = (response, fallbackMessage) => {
        if (response?.status === 403) {
            return "You don't have permission to do this.";
        }
        return response?.message || fallbackMessage;
    };

    const catalogModules = useMemo(() => {
        const combinedPermissions = new Set([
            ...Array.from(availablePermissions),
            ...Array.from(checkedPermissions),
        ]);

        return DISPLAY_MODULES.map((moduleItem) => {
            const availableActions = new Set();

            DISPLAY_ACTIONS.forEach((actionItem) => {
                const permissionKey = `${moduleItem.key}.${actionItem.key}`;
                if (combinedPermissions.has(permissionKey)) {
                    availableActions.add(actionItem.key);
                }
            });

            return {
                ...moduleItem,
                availableActions,
            };
        });
    }, [availablePermissions, checkedPermissions]);

    const knownCrudPermissions = useMemo(() => {
        const result = new Set();
        catalogModules.forEach((moduleItem) => {
            DISPLAY_ACTIONS.forEach((actionItem) => {
                if (!moduleItem.availableActions.has(actionItem.key)) return;
                result.add(`${moduleItem.key}.${actionItem.key}`);
            });
        });
        return result;
    }, [catalogModules]);

    const advancedPermissionKeys = useMemo(() => {
        const sourcePermissions = new Set([
            ...Array.from(availablePermissions),
            ...Array.from(checkedPermissions),
        ]);

        return Array.from(sourcePermissions)
            .filter(
                (permissionName) =>
                    !knownCrudPermissions.has(permissionName) &&
                    permissionName !== "permissions.manage",
            )
            .sort();
    }, [
        availablePermissions,
        checkedPermissions,
        knownCrudPermissions,
    ]);

    const getAdvancedPermissionLabel = (permissionName) => {
        const [modulePart, actionPart, ...rest] = permissionName.split(".");
        if (modulePart && actionPart && rest.length === 0) {
            const actionLabel =
                actionPart === "write" ? "Edit" : toTitleCase(actionPart);
            return `${actionLabel} ${toTitleCase(modulePart)}`;
        }

        return toTitleCase(permissionName);
    };

    const isPermissionLocked = (permissionName) => {
        return (selectedRole === "Admin" && permissionName === "permissions.manage") || !canManage || loading || saving || loadedRole !== selectedRole;
    };

    const isPermissionChecked = (permissionName) => {
        return checkedPermissions.has(permissionName);
    };

    useEffect(() => {
        const version = ++loadVersion.current;
        setLoadedRole(null);
        setCheckedPermissions(new Set());
        if (!canManage) {
            setLoading(permissionsLoading);
            return;
        }
        setLoading(true);
        (async () => {
            const [catalog, grants] = await Promise.all([
                getPermissions(), getRolePermissions(selectedRole),
            ]);
            if (loadVersion.current !== version) return;
            if (catalog.success && grants.success && Array.isArray(catalog.data) && Array.isArray(grants.data)) {
                const keys = new Set(normalizePermissionValues(grants.data));
                setAvailablePermissions(new Set(normalizePermissionValues(catalog.data)));
                setDescriptions(Object.fromEntries(catalog.data.flatMap((group) =>
                    (group.permissions || []).map((permission) => [normalizeKey(permission.key), permission.description])
                )));
                setCheckedPermissions(keys);
                setSavedPermissions(new Set(keys));
                setLoadedRole(selectedRole);
            } else {
                toast.error(getErrorMessage(!catalog.success ? catalog : grants, "Failed to load permissions."));
            }
            setLoading(false);
        })();
        return () => { loadVersion.current++; };
    }, [selectedRole, canManage, permissionsLoading]);

    const onTogglePermission = (permissionName) => {
        if (isPermissionLocked(permissionName)) return;

        setCheckedPermissions((prev) => {
            const next = new Set(prev);
            if (next.has(permissionName)) {
                next.delete(permissionName);
            } else {
                next.add(permissionName);
            }
            return next;
        });
    };

    const onSavePermissions = async () => {
        if (!canManage || loading || loadedRole !== selectedRole || saveInFlight.current) return;
        saveInFlight.current = true;
        setSaving(true);
        try {
            // Send exactly the selected grants, preserving protected grants as loaded.
            const response = await updateRolePermissions(selectedRole, Array.from(checkedPermissions));
            if (response.success && Array.isArray(response.data?.permissionKeys)) {
                const saved = new Set(normalizePermissionValues(response.data.permissionKeys));
                setCheckedPermissions(saved);
                setSavedPermissions(saved);
                toast.success("Permissions updated successfully.");
                await refreshPermissions();
            } else {
                toast.error(getErrorMessage(response, "Failed to update permissions."));
                if (response.status === 401 || response.status === 403) setLoadedRole(null);
            }
        } finally {
            saveInFlight.current = false;
            setSaving(false);
        }
    };

    return (
        <section>
            <Toaster />
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <PageContainer
                        isLoading={loading}
                        loadingText="Loading permissions..."
                    >
                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <div className="flex flex-col items-start max-w-3xl">
                                    <Breadcrumbs
                                        home="/settings"
                                        pages={[
                                            {
                                                name: "Permissions",
                                                href: "/admin/permissions",
                                                current: true,
                                            },
                                        ]}
                                    />

                                    <div className="w-full mt-12 mb-8 sm:flex sm:justify-between sm:items-center">
                                        <div className="mb-4 sm:mb-0">
                                            <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                                Role Permissions
                                            </h1>
                                            <p className="mt-2 text-sm text-slate-500">
                                                Configure global permissions by module.
                                            </p>
                                        </div>
                                    </div>

                                    {!permissionsLoading && !canManage && <p role="alert" className="mb-4 text-sm text-slate-600">An Admin with permissions.manage is required to edit role permissions.</p>}
                                    <div className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200">
                                        <header className="px-5 py-4 border-b border-slate-200">
                                            <h2 className="font-semibold text-slate-800">
                                                Permissions
                                            </h2>
                                        </header>

                                        <div className="px-5 py-4 space-y-6">
                                            <div>
                                                <label
                                                    htmlFor="roleSelector"
                                                    className="block text-sm font-medium text-gray-700"
                                                >
                                                    Role
                                                </label>
                                                <div className="w-full max-w-sm mt-1">
                                                    <select
                                                        disabled={saving || loading || !canManage}
                                                        id="roleSelector"
                                                        value={selectedRole}
                                                        onChange={(event) =>
                                                            setSelectedRole(
                                                                event.target.value,
                                                            )
                                                        }
                                                        className="w-full p-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                    >
                                                        {ROLES.map((roleName) => (
                                                            <option
                                                                key={roleName}
                                                                value={roleName}
                                                            >
                                                                {roleName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {catalogModules.map((moduleItem) => (
                                                <div
                                                    key={moduleItem.key}
                                                    className="border rounded-sm border-slate-200"
                                                >
                                                    <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                                                        <h3 className="font-semibold text-slate-800">
                                                            {moduleItem.title}
                                                        </h3>
                                                    </div>

                                                    <div className="px-4 py-3 space-y-3">
                                                        {DISPLAY_ACTIONS.filter(
                                                            (actionItem) =>
                                                                moduleItem.availableActions.has(
                                                                    actionItem.key,
                                                                ),
                                                        ).map((actionItem) => {
                                                            const permissionName = `${moduleItem.key}.${actionItem.key}`;
                                                            const locked =
                                                                isPermissionLocked(
                                                                    permissionName,
                                                                );
                                                            return (
                                                                <label
                                                                    key={
                                                                        permissionName
                                                                    }
                                                                    className="flex items-center justify-between gap-4"
                                                                >
                                                                    <span title={descriptions[permissionName]} className="text-sm text-slate-700">
                                                                        {
                                                                            actionItem.label
                                                                        }
                                                                        {descriptions[permissionName] && <span className="block text-xs text-slate-500">{descriptions[permissionName]}</span>}
                                                                    </span>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isPermissionChecked(
                                                                            permissionName,
                                                                        )}
                                                                        onChange={() =>
                                                                            onTogglePermission(
                                                                                permissionName,
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            locked
                                                                        }
                                                                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                                                    />
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}

                                            <div className="border rounded-sm border-slate-200">
                                                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                                                    <h3 className="font-semibold text-slate-800">
                                                        Manage Permissions
                                                    </h3>
                                                </div>

                                                <div className="px-4 py-3 space-y-3">
                                                    <label className="flex items-center justify-between gap-4">
                                                        <span className="text-sm text-slate-700">
                                                            Manage
                                                        </span>
                                                        <input
                                                            type="checkbox"
                                                            checked={isPermissionChecked(
                                                                "permissions.manage",
                                                            )}
                                                            onChange={() =>
                                                                onTogglePermission(
                                                                    "permissions.manage",
                                                                )
                                                            }
                                                            disabled={isPermissionLocked(
                                                                "permissions.manage",
                                                            )}
                                                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                                        />
                                                    </label>
                                                </div>
                                            </div>

                                            {advancedPermissionKeys.length > 0 && (
                                                <div className="border rounded-sm border-slate-200">
                                                    <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                                                        <h3 className="font-semibold text-slate-800">
                                                            Advanced
                                                        </h3>
                                                    </div>

                                                    <div className="px-4 py-3 space-y-3">
                                                        {advancedPermissionKeys.map(
                                                            (permissionName) => {
                                                                const locked =
                                                                    isPermissionLocked(
                                                                        permissionName,
                                                                    );
                                                                return (
                                                                    <label
                                                                        key={
                                                                            permissionName
                                                                        }
                                                                        className="flex items-center justify-between gap-4"
                                                                    >
                                                                        <span title={descriptions[permissionName]} className="text-sm text-slate-700">
                                                                            {getAdvancedPermissionLabel(
                                                                                permissionName,
                                                                            )}
                                                                        </span>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isPermissionChecked(
                                                                                permissionName,
                                                                            )}
                                                                            onChange={() =>
                                                                                onTogglePermission(
                                                                                    permissionName,
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                locked
                                                                            }
                                                                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                                                                        />
                                                                    </label>
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex justify-end w-full gap-3">
                                        <button
                                            onClick={() => setCheckedPermissions(new Set(savedPermissions))}
                                            type="button"
                                            disabled={saving || loading || !canManage || loadedRole !== selectedRole}
                                            className="z-50 inline-flex justify-center px-4 py-2 mt-4 text-sm font-medium bg-white border rounded-sm shadow-sm border-slate-300 text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-0 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            onClick={onSavePermissions}
                                            type="button"
                                            disabled={saving || loading || !canManage || loadedRole !== selectedRole}
                                            className="z-50 inline-flex justify-center px-4 py-2 mt-4 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {saving ? "Saving..." : "Save"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </PageContainer>
                </div>
            </div>
        </section>
    );
}

export default Permissions;
